import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    signal,
} from '@angular/core';
import { StateService, Transition, TransitionService } from '@uirouter/core';
import {
    SideNavigationMenuModel,
    SideNavigationMenuResolverService,
} from '@client/app/feature-modules/navigation/services/side-navigation-menu-resolver.service';
import { SideNavigationComponent } from './side-navigation.component';

@Component({
    selector: 'mon-side-navigation-container',
    standalone: true,
    imports: [SideNavigationComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <mon-side-navigation
            [menuItemsInput]="menuModel().items"
            [activeProfileInput]="menuModel().profile" />
    `,
})
export class SideNavigationContainerComponent {
    private readonly stateService = inject(StateService);
    private readonly transitionService = inject(TransitionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly sideNavigationMenuResolver = inject(SideNavigationMenuResolverService);

    protected readonly menuModel = signal<SideNavigationMenuModel>(
        this.resolveMenuModelForState(this.stateService.current?.name),
    );

    private resolveMenuModelForState (stateName?: string): SideNavigationMenuModel {
        if (!this.isSideNavigationState(stateName)) {
            return {
                profile: 'domains',
                items: [],
            };
        }

        return this.sideNavigationMenuResolver.resolve(stateName);
    }

    private isSideNavigationState (stateName?: string): boolean {
        return Boolean(
            stateName && (
                stateName === 'base.customer.domain' ||
                stateName.startsWith('base.customer.domain.') ||
                stateName === 'base.customer.admin' ||
                stateName.startsWith('base.customer.admin.')
            ),
        );
    }

    constructor () {
        const deregisterHook: unknown = this.transitionService.onSuccess({}, (transition: Transition) => {
            const stateName = transition.to().name;
            this.menuModel.set(this.resolveMenuModelForState(stateName));
        });

        this.destroyRef.onDestroy(() => {
            if (typeof deregisterHook === 'function') {
                (deregisterHook as () => void)();
            }
        });
    }
}
